import type { RichBlock, RichBlockType, RichInline, RichObjectKind, RichStyleName, RichTable } from './types';
import type { BlockIds } from './ids';
import { isNode } from './from-plate';

const BLOCK_TYPES: readonly string[] = [
  'paragraph',
  'heading',
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'quote',
  'codeBlock',
  'file',
  'columnList',
  'column',
  'divider',
  'toggleListItem',
  'table',
] satisfies RichBlockType[];

const OBJECT_KINDS: readonly string[] = ['task', 'channel', 'user', 'doc'] satisfies RichObjectKind[];

const STYLE_NAMES: readonly RichStyleName[] = ['bold', 'italic', 'underline', 'strike', 'code'];

function isTableContent(value: unknown): value is Record<string, unknown> {
  return isNode(value) && value.type === 'tableContent' && Array.isArray(value.rows);
}

export function isRichBlock(node: Record<string, unknown>): boolean {
  return Array.isArray(node.content) || (node.type === 'table' && (isTableContent(node.content) || isNode(node.table)));
}

function richStyles(value: unknown): Partial<Record<RichStyleName, true>> {
  const styles: Partial<Record<RichStyleName, true>> = {};
  if (isNode(value)) {
    for (const name of STYLE_NAMES) {
      if (value[name] === true) {
        styles[name] = true;
      }
    }
  }
  return styles;
}

function objectRef(props: Record<string, unknown>, label: string): RichInline {
  if (typeof props.kind === 'string' && OBJECT_KINDS.includes(props.kind) && typeof props.id === 'string') {
    return { type: 'objectRef', props: { kind: props.kind as RichObjectKind, id: props.id, label } };
  }
  return { type: 'text', text: label, styles: {} };
}

function richInline(items: unknown[]): RichInline[] {
  const out: RichInline[] = [];
  for (const item of items) {
    if (!isNode(item)) {
      continue;
    }
    if (item.type === 'text' && typeof item.text === 'string') {
      out.push({ type: 'text', text: item.text, styles: richStyles(item.styles) });
    } else if (item.type === 'link' && typeof item.href === 'string' && Array.isArray(item.content)) {
      out.push({ type: 'link', href: item.href, content: richInline(item.content) });
    } else if (item.type === 'mention' && isNode(item.props) && typeof item.props.handle === 'string') {
      out.push({ type: 'mention', props: { handle: item.props.handle } });
    } else if (item.type === 'objectRef' && isNode(item.props) && typeof item.props.label === 'string') {
      out.push(objectRef(item.props, item.props.label));
    }
  }
  return out;
}

function primitiveProps(value: unknown): RichBlock['props'] {
  const props: RichBlock['props'] = {};
  if (isNode(value)) {
    for (const [key, entry] of Object.entries(value)) {
      if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
        props[key] = entry;
      }
    }
  }
  return props;
}

function cellInline(cell: unknown): RichInline[] {
  if (Array.isArray(cell)) {
    return richInline(cell);
  }
  return isNode(cell) && Array.isArray(cell.content) ? richInline(cell.content) : [];
}

function tableRows(rows: unknown[]): RichInline[][][] {
  return rows.map((row) => {
    const cells = Array.isArray(row) ? row : isNode(row) && Array.isArray(row.cells) ? row.cells : [];
    return cells.map(cellInline);
  });
}

function headerCount(value: unknown): number {
  return typeof value === 'number' && value > 0 ? Math.floor(value) : 0;
}

function richTable(node: Record<string, unknown>): RichTable {
  const source = isTableContent(node.content) ? node.content : isNode(node.table) ? node.table : {};
  return {
    headerRows: headerCount(source.headerRows),
    headerCols: headerCount(source.headerCols),
    rows: Array.isArray(source.rows) ? tableRows(source.rows) : [],
  };
}

export function richBlock(node: Record<string, unknown>, ids: BlockIds): RichBlock {
  const known = typeof node.type === 'string' && BLOCK_TYPES.includes(node.type);
  const children = Array.isArray(node.children) ? node.children : [];
  return {
    id: ids.take(node.id),
    type: known ? (node.type as RichBlockType) : 'paragraph',
    props: known ? primitiveProps(node.props) : {},
    content: Array.isArray(node.content) ? richInline(node.content) : [],
    children: children.filter(isNode).map((child) => richBlock(child, ids)),
    ...(known && node.type === 'table' ? { table: richTable(node) } : {}),
  };
}
